// Moves every open tab onto a new release at the first safe moment.
//
// The Vite build stamps this bundle with __BUILD_ID__ and writes the same id
// to /version.json (served no-store); a mismatch means a newer build is live.
// The check runs 10s after boot, every 5 minutes, when the tab regains focus
// or comes back to the front, and on every page change.
//
// Once a newer build is known, the tab reloads at the first of:
//   - the next page change (the reload lands on the page being opened);
//   - the tab going to the background, or having just come back to the front;
//   - (back office) two minutes of no activity, or two minutes after the
//     "new version" note appeared, at the next pause of a few seconds.
// Never while something could be lost: input typed on this page (for 10
// minutes after the last keystroke, and for as long as a field the user
// filled in is still on screen holding it), a save still on its way to the
// server, a focused field or payment frame, an open dialog, playing media, or
// a checkout, sign-up or payment-link page. Those wait for a later moment —
// at the latest the next page change.
//
// A shopper on a store is reloaded only on a page change or while the tab is
// hidden — never in front of them, and never with a note.
//
// Before this, the tab waited for a page change inside the same layout, so
// anyone who stayed on one screen kept the old build for as long as the tab
// stayed open.

/* global __BUILD_ID__ */
import { showUpdatingOverlay } from "./chunkRecovery";
import { isMaskedHost } from "./maskedDomain";

const CHECK_THROTTLE_MS = 30_000;
const CHECK_INTERVAL_MS = 5 * 60_000;
const TICK_MS = 15_000;
const IDLE_MS = 2 * 60_000; // no input this long = nobody is mid-task
const NOTE_GRACE_MS = 2 * 60_000; // after the note shows, apply at a pause
const PAUSE_MS = 5_000; // "a pause": no input for this long
const TYPING_HOLD_MS = 10 * 60_000; // recent typing protects the page this long
const JUST_SHOWN_MS = 3_000; // came back to the tab this recently
// Automatic reloads per new build per tab, at least RELOAD_RETRY_MS apart. A
// reload can land on the old build while a deploy is still switching over;
// a few spaced tries get past that without ever looping.
const ATTEMPTS_KEY = "arego_update_attempts";
const MAX_AUTO_RELOADS = 3;
const RELOAD_RETRY_MS = 2 * 60_000;

// Pages where a reload can lose a payment, a sign-up or an intake form even
// without typing (card fields live in a payment provider's frame).
const PROTECTED_PATH = /(^|\/)(checkout|thankyou|register|enroll|sponsor|portal)(\/|$)/i;
// Store pages a guest browses, on the office host. (A store's own domain is
// all storefront: isMaskedHost.)
const STOREFRONT_PATH = /^\/marketplace\/(?!manage(\/|$))(?!subscriptions(\/|$))(?!orders(\/|$))(?!points(\/|$))(?!form-collection(\/|$))(?!preview(\/|$))[^/]+/i;

let pendingBuild = null;
let pendingSince = 0;
let lastCheckAt = 0;
let lastPath = typeof window !== "undefined" ? window.location.pathname : "";
let lastNavAt = 0;
let lastActivityAt = Date.now();
let lastTypedAt = 0;
let typedPath = ""; // the page the user last typed on
let typedFields = new Set(); // fields they typed into on that page
let shownAt = Date.now();
let started = false;
let writesInFlight = 0;

const currentBuild = () => {
  try { return typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : ""; } catch { return ""; }
};

const isShopper = () => {
  try { return isMaskedHost() || STOREFRONT_PATH.test(window.location.pathname); } catch { return false; }
};

export function isUpdatePending() {
  return pendingBuild != null;
}

// A field the user changed that is still on the page and still holds it.
const stillHolds = (el) => {
  if (!el.isConnected) return false;
  if (el.isContentEditable) return (el.textContent || "").trim() !== "";
  if (el.tagName === "SELECT" || el.type === "checkbox" || el.type === "radio") return true;
  return String(el.value ?? "").trim() !== "";
};

// True while something the user typed on this page could be lost by a reload
// or overwritten by a silent data refresh: for 10 minutes after the last
// keystroke, and after that for as long as a field they filled in is still
// on screen with something in it. A page change ends it — the fields left
// with the page.
export function pageHasUnsavedInput() {
  if (!typedPath || typedPath !== window.location.pathname) return false;
  if (Date.now() - lastTypedAt < TYPING_HOLD_MS) return true;
  for (const el of typedFields) {
    if (stillHolds(el)) return true;
  }
  return false;
}

// Checkout, sign-up, member-portal and payment-link pages: a reload there can
// lose a payment or an intake form even without typing (card fields live in
// a payment provider's frame).
export function isProtectedPage() {
  return PROTECTED_PATH.test(window.location.pathname) || /[?&]pr=/.test(window.location.search);
}

// Why reloading right now could lose something; "" when it is safe.
function blockedReason() {
  if (pageHasUnsavedInput()) return "typing";
  // A reload would drop the answer to a save or payment still in flight, and
  // the user might then repeat it.
  if (writesInFlight > 0) return "saving";
  if (isProtectedPage()) return "checkout";
  const ae = document.activeElement;
  if (ae && ae !== document.body && (ae.tagName === "IFRAME" || ae.isContentEditable || ae.matches?.("input, textarea, select"))) {
    return "focused field";
  }
  if (document.querySelector('.modal.show, .offcanvas.show, dialog[open], [aria-modal="true"]')) return "dialog";
  for (const m of document.querySelectorAll("video, audio")) {
    if (!m.paused && !m.ended) return "media";
  }
  return "";
}

// The tab's automatic reloads for this build so far: { build, n, at }. null
// when session storage is unavailable — then there is no way to count, so
// there are no automatic reloads (the note's button still works).
function readAttempts(build) {
  let raw;
  try { raw = sessionStorage.getItem(ATTEMPTS_KEY); } catch { return null; }
  try {
    const rec = JSON.parse(raw || "null");
    if (rec && rec.build === build && Number.isFinite(rec.n)) return rec;
  } catch { /* unreadable: start over */ }
  return { build, n: 0, at: 0 };
}

// Reloads onto the new build if this tab still has a try left for it. The
// first try is a plain reload; a later one (the last came back on the old
// build) asks for the page by a fresh address.
function reloadOnto(build) {
  const rec = readAttempts(build);
  if (!rec || rec.n >= MAX_AUTO_RELOADS) return false;
  if (rec.n > 0 && Date.now() - rec.at < RELOAD_RETRY_MS) return false;
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify({ build, n: rec.n + 1, at: Date.now() }));
  } catch {
    return false;
  }
  removeNote();
  showUpdatingOverlay();
  if (rec.n > 0) {
    const u = new URL(window.location.href);
    u.searchParams.set("cb", build); // main.jsx strips it again at boot
    window.location.replace(u.toString());
  } else {
    window.location.reload();
  }
  return true;
}

export function applyPendingUpdate() {
  if (!pendingBuild) return false;
  return reloadOnto(pendingBuild);
}

// trigger: "navigation" | "hidden" | "shown" | "tick" | "detected"
function consider(trigger) {
  if (!pendingBuild) return;
  if (blockedReason()) {
    if (!isShopper()) showNote();
    return;
  }
  const now = Date.now();
  const hidden = document.visibilityState === "hidden";
  const justShown = now - shownAt < JUST_SHOWN_MS;
  // A check started by a page change answers a moment after it: still that
  // page change.
  const justNavigated = now - lastNavAt < JUST_SHOWN_MS;
  const shopper = isShopper();
  if (trigger === "navigation" || justNavigated || hidden || (justShown && !shopper)) {
    if (!applyPendingUpdate() && !shopper) showNote();
    return;
  }
  if (shopper) return; // shoppers: a page change or a hidden tab only
  const idle = now - lastActivityAt >= IDLE_MS;
  const paused = now - lastActivityAt >= PAUSE_MS;
  const graceOver = pendingSince && now - pendingSince >= NOTE_GRACE_MS;
  if ((idle || (graceOver && paused)) && applyPendingUpdate()) return;
  showNote();
}

// The back office's slim note: what is happening, and a way to do it now.
function showNote() {
  const text = pageHasUnsavedInput()
    ? "It loads when you leave this page."
    : "This page updates itself at a quiet moment.";
  const existing = document.getElementById("agp-update-note");
  if (existing) {
    const sub = existing.querySelector("[data-agp-sub]");
    if (sub && sub.textContent !== text) sub.textContent = text;
    return;
  }
  const el = document.createElement("div");
  el.id = "agp-update-note";
  el.setAttribute("role", "status");
  el.setAttribute("style",
    "position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:99998;background:#1d1c19;color:#fff;" +
    "border-radius:999px;padding:8px 8px 8px 18px;box-shadow:0 8px 28px rgba(0,0,0,.18);" +
    "font-family:system-ui,-apple-system,'Segoe UI',sans-serif;font-size:13.5px;display:flex;align-items:center;gap:12px;" +
    "max-width:calc(100vw - 32px);");
  el.innerHTML =
    '<span><b>A new version is ready.</b> <span data-agp-sub style="opacity:.75"></span></span>' +
    '<button type="button" id="agp-update-now" style="background:#fff;color:#1d1c19;border:0;border-radius:999px;padding:6px 14px;font-weight:600;cursor:pointer;flex:none">Update now</button>';
  el.querySelector("[data-agp-sub]").textContent = text;
  (document.body || document.documentElement).appendChild(el);
  // The user asked for it: no reload budget, and the only guard is a warning
  // when something they typed here would be lost.
  el.querySelector("#agp-update-now").onclick = () => {
    if (pageHasUnsavedInput()
      && !window.confirm("Update now? Anything you typed on this page and have not saved yet will be lost.")) return;
    removeNote();
    showUpdatingOverlay();
    window.location.reload();
  };
}

function removeNote() {
  document.getElementById("agp-update-note")?.remove();
}

export async function checkForUpdate(force = false) {
  const mine = currentBuild();
  if (!mine || pendingBuild) return;
  const now = Date.now();
  if (!force && now - lastCheckAt < CHECK_THROTTLE_MS) return;
  lastCheckAt = now;
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return;
    const data = await res.json();
    if (data?.build && data.build !== mine) {
      pendingBuild = String(data.build);
      pendingSince = Date.now();
      consider("detected");
    }
  } catch {
    // Network hiccup or a deploy mid-swap: try again later.
  }
}

// Called by the router's lazyRoute wrappers before a route's code loads.
export function noteNavigation() {
  checkForUpdate();
}

// Called for every committed location (App.jsx subscribes both routers). A
// changed path is a page change: the moment to move onto a pending build.
export function noteLocation(pathname) {
  if (!pathname || pathname === lastPath) return;
  lastPath = pathname;
  lastNavAt = Date.now();
  if (pendingBuild) {
    consider("navigation");
  } else {
    checkForUpdate();
  }
}

const markActive = () => { lastActivityAt = Date.now(); };

const markTyped = (e) => {
  const t = e.target;
  if (!t || t === document.body) return;
  if (!(t.isContentEditable || t.matches?.("input, textarea, select"))) return;
  const path = window.location.pathname;
  if (path !== typedPath) {
    typedPath = path;
    typedFields = new Set();
  }
  typedFields.add(t);
  lastTypedAt = Date.now();
};

// Counts requests that change something (anything but GET/HEAD) while they
// are on the wire. GraphQL queries are POSTs too; holding a reload for one of
// those only delays it.
function trackWrites() {
  const isWrite = (m) => {
    const method = String(m || "GET").toUpperCase();
    return method !== "GET" && method !== "HEAD";
  };
  const origFetch = window.fetch;
  if (typeof origFetch === "function") {
    // Always called on window: fetch throws "Illegal invocation" on any
    // other receiver.
    window.fetch = function trackedFetch(input, init) {
      const method = init?.method ?? (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET");
      if (!isWrite(method)) return origFetch.call(window, input, init);
      writesInFlight++;
      let settled = false;
      const done = () => { if (!settled) { settled = true; writesInFlight--; } };
      try {
        const p = origFetch.call(window, input, init);
        p.then(done, done);
        return p;
      } catch (err) {
        done();
        throw err;
      }
    };
  }
  const proto = window.XMLHttpRequest?.prototype;
  if (proto) {
    const origOpen = proto.open;
    const origSend = proto.send;
    proto.open = function trackedOpen(method, ...rest) {
      this.__aregoWrite = isWrite(method);
      return origOpen.call(this, method, ...rest);
    };
    proto.send = function trackedSend(...args) {
      if (!this.__aregoWrite) return origSend.apply(this, args);
      writesInFlight++;
      let settled = false;
      const done = () => { if (!settled) { settled = true; writesInFlight--; } };
      this.addEventListener("loadend", done, { once: true });
      try {
        return origSend.apply(this, args);
      } catch (err) {
        done(); // never sent, so no loadend will come
        throw err;
      }
    };
  }
}

export function startVersionWatchdog() {
  if (import.meta.env.DEV || started) return;
  if (!currentBuild()) return;
  started = true;
  trackWrites();

  const opts = { capture: true, passive: true };
  for (const ev of ["pointerdown", "keydown", "wheel", "touchstart", "mousemove", "scroll"]) {
    window.addEventListener(ev, markActive, opts);
  }
  document.addEventListener("input", markTyped, true);
  document.addEventListener("change", markTyped, true);

  setInterval(() => checkForUpdate(), CHECK_INTERVAL_MS);
  setInterval(() => consider("tick"), TICK_MS);
  window.addEventListener("focus", () => checkForUpdate());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      shownAt = Date.now();
      if (pendingBuild) consider("shown");
      else checkForUpdate();
    } else {
      consider("hidden");
    }
  });
  // First probe shortly after boot: catches "this tab booted from an HTML
  // copy that was already one build behind".
  setTimeout(() => checkForUpdate(true), 10_000);
}
